/** Single animated bar in the chart */

import { plotFraction } from '@parlay-picker/shared/chartScale';
import { betOutcome } from '@parlay-picker/shared/lineOutcome';
import { STAT_SHORT_LABELS } from '@parlay-picker/shared/statsChart';
import { cn } from '@utils/cn';
import { AnimatePresence, motion } from 'framer-motion';
import { type CSSProperties, type ReactElement, useState } from 'react';
import { OUTCOME_STYLE, outcomeGradient } from './outcomeStyles';
import type { ChartDataPoint } from './types';

/** A DNP game's stub, as a percent of the plot's height */
const DNP_BAR_PERCENT = 2;
/** A played game's bar is at least this percent of the plot's height */
const MIN_BAR_PERCENT = 3;

interface AnimatedBarProps {
  point: ChartDataPoint;
  maxValue: number;
  minValue: number;
  propLine: number;
  isUnderBet: boolean;
  isAlternateLine: boolean;
  isExpanded: boolean;
  onClick: () => void;
  index: number;
  barCount: number;
  statKey: string;
}

export function AnimatedBar({
  point,
  maxValue,
  minValue,
  propLine,
  isUnderBet,
  isAlternateLine,
  isExpanded,
  onClick,
  index,
  barCount,
  statKey,
}: AnimatedBarProps): ReactElement {
  const [showTooltip, setShowTooltip] = useState(false);
  const outcome = point.dnp
    ? null
    : betOutcome(point.value, propLine, isUnderBet ? 'under' : 'over', isAlternateLine);
  // DNP bars: tiny stub at bottom; normal bars: scale within range
  const heightPercent = point.dnp
    ? DNP_BAR_PERCENT
    : Math.max(MIN_BAR_PERCENT, plotFraction(point.value, minValue, maxValue) * 100);

  // Colors — gradient fill from darker at bottom to lighter at top
  const glowColor = outcome ? OUTCOME_STYLE[outcome].glow : '';
  const barStyle: CSSProperties = !outcome
    ? { minHeight: '4px' }
    : {
        minHeight: '8px',
        background: outcomeGradient(outcome),
        // Mute bars that are using full-game fallback data instead of period data
        ...(point.hasPeriodData === false && { opacity: 0.35 }),
      };

  const shortLabel = STAT_SHORT_LABELS[statKey] || statKey;

  return (
    <div
      className={cn(
        'relative flex-1 min-w-0 cursor-pointer group',
        barCount > 10 ? 'max-w-8' : 'max-w-12',
      )}
      onClick={onClick}
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      {/* Value label — absolute above bar area */}
      <motion.span
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: index * 0.04 + 0.3 }}
        className={cn(
          'absolute -top-3.5 left-1/2 -translate-x-1/2 text-[9px] font-bold leading-none whitespace-nowrap z-10',
          outcome ? OUTCOME_STYLE[outcome].text : 'text-theme-text-tertiary',
        )}
      >
        {point.dnp ? 'DNP' : point.value}
      </motion.span>

      {/* Bar — fills the full flex item height so percentages match the grid area */}
      <motion.div
        initial={{ height: 0 }}
        animate={{ height: `${heightPercent}%` }}
        transition={{
          type: 'spring',
          stiffness: 300,
          damping: 25,
          delay: index * 0.04,
        }}
        className={cn(
          'w-full absolute bottom-0 rounded-t-md transition-shadow',
          point.dnp && 'bg-theme-surface-active',
          isExpanded ? `shadow-lg ${glowColor}` : `group-hover:shadow-md group-hover:${glowColor}`,
        )}
        style={barStyle}
      />

      {/* X-axis label — absolute below bar area */}
      <span
        className={cn(
          'absolute -bottom-4 left-1/2 -translate-x-1/2 text-[9px] text-theme-text-tertiary leading-none whitespace-nowrap text-center',
          barCount > 5 && 'text-[8px]',
        )}
      >
        {barCount > 5 ? point.compactLabel : point.label}
      </span>

      {/* Tooltip on hover — position shifts based on bar's position to prevent overflow */}
      <AnimatePresence>
        {showTooltip && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            transition={{ duration: 0.15 }}
            className={cn(
              'absolute bottom-full mb-2 z-50 pointer-events-none',
              // First 2 bars: align left edge to bar; last 2: align right edge; else center
              index <= 1
                ? 'left-0'
                : index >= barCount - 2
                  ? 'right-0'
                  : 'left-1/2 -translate-x-1/2',
            )}
          >
            <div
              className={cn(
                'rounded-lg px-3 py-2 shadow-lg border whitespace-nowrap',
                outcome ? OUTCOME_STYLE[outcome].tooltip : 'bg-theme-surface border-theme-border',
              )}
            >
              <div className="text-xs text-theme-text-secondary mb-1">
                {point.dateFormatted || point.label}
              </div>
              {point.opponent && (
                <div className="text-xs text-theme-text-tertiary mb-1">
                  {point.isHome !== undefined && (
                    <span className="text-[10px] mr-1">{point.isHome ? '@' : 'vs'}</span>
                  )}
                  {point.opponent}
                </div>
              )}
              {/* Game result (if team data available) */}
              {point.teamScore !== undefined && point.opponentScore !== undefined && (
                <div className="text-xs mb-1 flex items-center gap-1.5">
                  <span
                    className={cn(
                      'font-semibold',
                      point.won ? 'text-theme-success' : 'text-theme-danger',
                    )}
                  >
                    {point.won ? 'W' : 'L'}
                  </span>
                  <span className="text-theme-text-tertiary">
                    {point.teamScore}-{point.opponentScore}
                  </span>
                </div>
              )}
              {!outcome ? (
                <div className="text-sm font-bold text-theme-text-tertiary">DNP</div>
              ) : (
                <div className="flex items-center gap-2">
                  <span className={cn('text-sm font-bold', OUTCOME_STYLE[outcome].text)}>
                    {point.value} {shortLabel}
                  </span>
                  {point.min && (
                    <span className="text-[10px] text-theme-text-tertiary">{point.min} min</span>
                  )}
                </div>
              )}
            </div>
            {/* Tooltip arrow — shifts to match tooltip alignment */}
            <div
              className={cn(
                'absolute top-full w-0 h-0',
                'border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent',
                index <= 1
                  ? 'left-2'
                  : index >= barCount - 2
                    ? 'right-2'
                    : 'left-1/2 -translate-x-1/2',
                outcome ? OUTCOME_STYLE[outcome].arrow : 'border-t-theme-border',
              )}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
